import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { PartnerTier } from "@modules/partners/types";
import { tierSchema, TierFormValues } from "@modules/partners/validations";
import {
  useCreateTierMutation,
  useEditTierMutation,
} from "@modules/partners/services/mutations";

interface TierFormModalProps {
  open: boolean;
  tier?: PartnerTier | null;
  onClose: () => void;
}

export function TierFormModal({ open, tier, onClose }: TierFormModalProps) {
  const translate = useTranslate();
  const createMutation = useCreateTierMutation();
  const editMutation = useEditTierMutation();

  const methods = useForm<TierFormValues>({
    resolver: zodResolver(tierSchema()),
    mode: "onBlur",
  });

  useEffect(() => {
    if (!open) return;
    methods.reset(
      (tier
        ? {
            name: tier.name,
            rank: tier.rank,
            // string cru ("100.00") — a máscara de moeda formata na exibição.
            min_sales_value: tier.min_sales_value,
            commission_percentage: Number(tier.commission_percentage),
          }
        : { name: "", rank: 0, min_sales_value: 0, commission_percentage: 0 }) as unknown as TierFormValues
    );
  }, [open, tier, methods]);

  const isPending = createMutation.isPending || editMutation.isPending;

  const onSubmit = methods.handleSubmit(async (values) => {
    if (tier) {
      await editMutation.mutateAsync({ id: tier.id, values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {translate(
          tier ? "v1/partner_tier_admin.titles.edit" : "v1/partner_tier_admin.titles.create"
        )}
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <Form.Input
                name="name"
                label={translate("v1/partner_tier_admin.fields.name")}
                required
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Form.Input
                name="rank"
                label={translate("v1/partner_tier_admin.fields.rank")}
                required
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Form.Input
                name="min_sales_value"
                label={translate("v1/partner_tier_admin.fields.min_sales_value")}
                mask="currency"
                required
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Form.Input
                name="commission_percentage"
                label={translate("v1/partner_tier_admin.fields.commission_percentage")}
                required
              />
            </Grid>
          </Grid>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate("buttons.cancel")}</Button>
        <Button variant="contained" onClick={onSubmit} disabled={isPending}>
          {translate("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
